// HTTP contract between the PWA and the server, plus the input validation both
// sides share. Change this file first when the API changes.
import { isDayKey, type DayKey, type Weekday } from './days';
import { isStickerId } from './stickers';

export type { DayKey, Weekday } from './days';

export const HABIT_COLORS = ['blue', 'sky', 'teal', 'green', 'lime', 'amber', 'orange', 'rose', 'pink', 'violet'] as const;
export type HabitColor = (typeof HABIT_COLORS)[number];

/**
 * daily: every day. weekdays: on the listed days only. weekly: `times` days per
 * week (Monday–Sunday), on any days.
 */
export type Schedule = { kind: 'daily'; times?: number } | { kind: 'weekdays'; days: Weekday[] } | { kind: 'weekly'; times: number };

export interface Habit {
  id: string;
  name: string;
  emoji: string;
  /** Sticker id (shared/stickers.ts) shown instead of the emoji; missing in caches of older versions. */
  sticker?: string | null;
  color: HabitColor;
  schedule: Schedule;
  /** Paused: hidden from "Heute", history stays. */
  archived: boolean;
  position: number;
  /** First day the habit counts (the local day it was created). */
  startDay: DayKey;
}

export interface HabitInput {
  name: string;
  emoji: string;
  /** null or missing: no sticker. In an update, null removes it. */
  sticker?: string | null;
  color: HabitColor;
  schedule: Schedule;
}

export interface CreateHabitRequest extends HabitInput {
  /** The client's local day; becomes `startDay`. */
  today: DayKey;
}

export type UpdateHabitRequest = Partial<HabitInput> & { archived?: boolean };

export interface ReorderRequest {
  ids: string[];
}

export interface SetCompletionRequest {
  habitId: string;
  day: DayKey;
  count: number;
}

export interface DataResponse {
  habits: Habit[];
  /** Completed days per habit id, ascending. */
  completions: Record<string, DayKey[]>;
  /** Completion counts per habit and day; older cached responses imply one. */
  completionCounts?: Record<string, Record<string, number>>;
}

export interface AppUser {
  id: string;
  email: string;
  name?: string;
}

export interface MeResponse {
  user: AppUser | null;
}

export type ErrorCode = 'bad_request' | 'login_required' | 'not_found' | 'unavailable' | 'internal';

export interface ApiError {
  error: { code: ErrorCode; message: string };
}

// ---------------------------------------------------------------------------
// Validation

export const NAME_MAX = 60;
export const MAX_HABITS = 100;

export type Parsed<T> = { ok: true; value: T } | { ok: false; message: string };

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const isUuid = (v: unknown): v is string => typeof v === 'string' && UUID_RE.test(v);

/** One emoji (possibly a ZWJ sequence or with modifiers), no letters or digits. */
export function isEmoji(value: unknown): value is string {
  if (typeof value !== 'string' || value.length === 0 || value.length > 16) return false;
  const segments = [...new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(value)];
  return segments.length === 1 && /\p{Extended_Pictographic}|\p{Regional_Indicator}/u.test(value);
}

export function normalizeName(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

export function parseSchedule(value: unknown): Parsed<Schedule> {
  if (!isObject(value)) return { ok: false, message: 'Ungültige Häufigkeit.' };
  switch (value.kind) {
    case 'daily': {
      const times = value.times;
      if (times === undefined || times === 1) return { ok: true, value: { kind: 'daily' } };
      if (!Number.isInteger(times) || (times as number) < 2 || (times as number) > 20) {
        return { ok: false, message: 'Wähle 1 bis 20 Mal pro Tag.' };
      }
      return { ok: true, value: { kind: 'daily', times: times as number } };
    }
    case 'weekdays': {
      const days = value.days;
      if (!Array.isArray(days) || days.some((d) => !Number.isInteger(d) || d < 0 || d > 6)) {
        return { ok: false, message: 'Ungültige Wochentage.' };
      }
      const unique = [...new Set(days as Weekday[])].sort((a, b) => a - b);
      if (unique.length === 0) return { ok: false, message: 'Wähle mindestens einen Wochentag.' };
      if (unique.length === 7) return { ok: true, value: { kind: 'daily' } };
      return { ok: true, value: { kind: 'weekdays', days: unique } };
    }
    case 'weekly': {
      const times = value.times;
      if (!Number.isInteger(times) || (times as number) < 1 || (times as number) > 7) {
        return { ok: false, message: 'Wähle 1 bis 7 Mal pro Woche.' };
      }
      return times === 7 ? { ok: true, value: { kind: 'daily' } } : { ok: true, value: { kind: 'weekly', times: times as number } };
    }
    default:
      return { ok: false, message: 'Ungültige Häufigkeit.' };
  }
}

function parseField<K extends keyof HabitInput>(key: K, value: unknown): Parsed<HabitInput[K]> {
  switch (key) {
    case 'name': {
      if (typeof value !== 'string') return { ok: false, message: 'Ungültiger Name.' };
      const name = normalizeName(value);
      if (!name) return { ok: false, message: 'Gib dem Habit einen Namen.' };
      if ([...name].length > NAME_MAX) return { ok: false, message: `Der Name darf höchstens ${NAME_MAX} Zeichen lang sein.` };
      return { ok: true, value: name as HabitInput[K] };
    }
    case 'emoji':
      return isEmoji(value) ? { ok: true, value: value as HabitInput[K] } : { ok: false, message: 'Wähle ein Emoji.' };
    case 'sticker':
      if (value === undefined || value === null) return { ok: true, value: null as HabitInput[K] };
      return isStickerId(value) ? { ok: true, value: value as HabitInput[K] } : { ok: false, message: 'Unbekannter Sticker.' };
    case 'color':
      return HABIT_COLORS.includes(value as HabitColor)
        ? { ok: true, value: value as HabitInput[K] }
        : { ok: false, message: 'Ungültige Farbe.' };
    case 'schedule':
      return parseSchedule(value) as Parsed<HabitInput[K]>;
    default:
      return { ok: false, message: 'Unbekanntes Feld.' };
  }
}

const INPUT_KEYS = ['name', 'emoji', 'sticker', 'color', 'schedule'] as const;

export function parseCreateHabit(value: unknown): Parsed<CreateHabitRequest> {
  if (!isObject(value)) return { ok: false, message: 'Ungültige Anfrage.' };
  const out: Partial<CreateHabitRequest> = {};
  for (const key of INPUT_KEYS) {
    const parsed = parseField(key, value[key]);
    if (!parsed.ok) return parsed;
    (out as Record<string, unknown>)[key] = parsed.value;
  }
  if (!isDayKey(value.today)) return { ok: false, message: 'Ungültiges Datum.' };
  out.today = value.today;
  return { ok: true, value: out as CreateHabitRequest };
}

export function parseUpdateHabit(value: unknown): Parsed<UpdateHabitRequest> {
  if (!isObject(value)) return { ok: false, message: 'Ungültige Anfrage.' };
  const out: UpdateHabitRequest = {};
  for (const key of INPUT_KEYS) {
    if (value[key] === undefined) continue;
    const parsed = parseField(key, value[key]);
    if (!parsed.ok) return parsed;
    (out as Record<string, unknown>)[key] = parsed.value;
  }
  if (value.archived !== undefined) {
    if (typeof value.archived !== 'boolean') return { ok: false, message: 'Ungültiger Status.' };
    out.archived = value.archived;
  }
  if (Object.keys(out).length === 0) return { ok: false, message: 'Keine Änderungen.' };
  return { ok: true, value: out };
}

export function parseReorder(value: unknown): Parsed<ReorderRequest> {
  if (!isObject(value) || !Array.isArray(value.ids)) return { ok: false, message: 'Ungültige Reihenfolge.' };
  const ids = value.ids;
  if (ids.length > MAX_HABITS || !ids.every(isUuid) || new Set(ids).size !== ids.length) {
    return { ok: false, message: 'Ungültige Reihenfolge.' };
  }
  return { ok: true, value: { ids } };
}

export function parseSetCompletion(value: unknown): Parsed<SetCompletionRequest> {
  if (!isObject(value)) return { ok: false, message: 'Ungültige Anfrage.' };
  if (!isUuid(value.habitId)) return { ok: false, message: 'Ungültiges Habit.' };
  if (!isDayKey(value.day)) return { ok: false, message: 'Ungültiges Datum.' };
  const count = value.count ?? (typeof value.done === 'boolean' ? (value.done ? 1 : 0) : undefined);
  if (!Number.isInteger(count) || (count as number) < 0 || (count as number) > 20) return { ok: false, message: 'Ungültige Anzahl.' };
  return { ok: true, value: { habitId: value.habitId, day: value.day, count: count as number } };
}
